import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as newcomerRewardService from './service';
import type { NewcomerRewardDTO } from './types';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  network_error: '网络连接失败，请检查网络',
  'invalid newcomer reward status': '无效的新人奖励状态',
  'invalid newcomer reward range': '无效的时间范围',
  'invalid user_id': '无效的用户 ID',
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    return ERROR_MAP[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useNewcomerRewardList(enabled = true) {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('newcomerUserId') || '';
  const status = searchParams.get('newcomerStatus') || '';
  const range = searchParams.get('newcomerRange') || '';
  const page = parseInt(searchParams.get('newcomerPage') || '1', 10) || 1;

  const [items, setItems] = useState<NewcomerRewardDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (p: number, uid: string, st: string, rg: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await newcomerRewardService.listNewcomerRewards({
        page: p,
        pageSize,
        userId: uid,
        status: st,
        range: rg,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    if (!enabled) return;
    load(page, userId, status, range);
  }, [enabled, load, page, userId, status, range]);

  const setUserId = useCallback((v: string) => {
    updateParams({ newcomerUserId: v || null, newcomerPage: null });
  }, [updateParams]);

  const setStatus = useCallback((v: string) => {
    updateParams({ newcomerStatus: v || null, newcomerPage: null });
  }, [updateParams]);

  const setRange = useCallback((v: string) => {
    updateParams({ newcomerRange: v || null, newcomerPage: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ newcomerPage: value > 1 ? String(value) : null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    updateParams({
      newcomerUserId: null,
      newcomerStatus: null,
      newcomerRange: null,
      newcomerPage: null,
    });
  }, [updateParams]);

  const reload = useCallback(() => {
    load(page, userId, status, range);
  }, [load, page, userId, status, range]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items, total, page, pageSize, totalPages,
    userId, status, range,
    loading, error,
    setPage, setUserId, setStatus, setRange,
    resetFilters, reload,
  };
}
